import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import JSZip from 'jszip';
import { useAuth } from '../utils/AuthContext';
import AppHeader from '../components/ui/AppHeader';
import { subscribeToSettings } from '../utils/appSettings';
import {
  Image,
  Download,
  Upload,
  X,
  AlertTriangle,
  Wrench,
  FileImage,
  Scissors,
  ExternalLink,
  Target,
  Ruler,
} from 'lucide-react';
import {
  compressImageToWebP,
  getWebpFilename,
  isSupportedImageType,
} from '../utils/imageCompression';
import { formatFileSize } from '../utils/mediaLibrary';
import './ToolsPage.css';

const ToolsPage: React.FC = () => {
  const { user } = useAuth();
  const isFullCrmUser = user?.role === 'employee' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';
  const [handCutVsCncExternalUrl, setHandCutVsCncExternalUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [compressorFiles, setCompressorFiles] = useState<File[]>([]);
  const [compressedResults, setCompressedResults] = useState<{ file: File; blob: Blob }[]>([]);
  const [compressorQuality, setCompressorQuality] = useState(0.82);
  const [compressorMaxWidth, setCompressorMaxWidth] = useState<number | ''>(1920);
  const [compressorMaxHeight, setCompressorMaxHeight] = useState<number | ''>(1920);
  const [compressorProcessing, setCompressorProcessing] = useState(false);
  const [compressorProgress, setCompressorProgress] = useState<{ current: number; total: number } | null>(null);
  const [compressorError, setCompressorError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFullCrmUser) return;
    const unsub = subscribeToSettings((settings) => {
      if (settings.handCutVsCncComparisonUrl !== undefined) {
        setHandCutVsCncExternalUrl(settings.handCutVsCncComparisonUrl || '');
      }
    });
    return unsub;
  }, [isFullCrmUser]);

  const isBulk = compressorFiles.length > 1;

  const addFiles = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const supported = fileArray.filter((f) => isSupportedImageType(f));
    const unsupported = fileArray.filter((f) => !isSupportedImageType(f));
    if (supported.length > 0) {
      setCompressorFiles((prev) => [...prev, ...supported]);
      setCompressedResults([]);
      setCompressorError(null);
    }
    if (unsupported.length > 0) {
      setCompressorError(`Skipped ${unsupported.length} unsupported file(s). Use JPEG, PNG, GIF, WebP, BMP, or HEIC.`);
    }
  };

  const removeFile = (index: number) => {
    setCompressorFiles((prev) => prev.filter((_, i) => i !== index));
    setCompressedResults([]);
    setCompressorError(null);
  };

  const clearAll = () => {
    setCompressorFiles([]);
    setCompressedResults([]);
    setCompressorError(null);
  };

  const processAll = async () => {
    if (compressorFiles.length === 0) return;
    setCompressorProcessing(true);
    setCompressorError(null);
    setCompressorProgress({ current: 0, total: compressorFiles.length });

    const results: { file: File; blob: Blob }[] = [];
    const failed: string[] = [];
    const options = {
      quality: compressorQuality,
      maxWidth: compressorMaxWidth === '' ? undefined : compressorMaxWidth,
      maxHeight: compressorMaxHeight === '' ? undefined : compressorMaxHeight,
    };

    try {
      for (let i = 0; i < compressorFiles.length; i++) {
        setCompressorProgress({ current: i + 1, total: compressorFiles.length });
        try {
          const blob = await compressImageToWebP(compressorFiles[i], options);
          results.push({ file: compressorFiles[i], blob });
        } catch (err) {
          failed.push(compressorFiles[i].name);
        }
      }
      setCompressedResults(results);
      if (failed.length > 0) {
        setCompressorError(
          results.length > 0
            ? `Skipped ${failed.length} file(s) that couldn't be converted: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? ` +${failed.length - 3} more` : ''}. Some HEIC files from newer iPhones may not be supported.`
            : `Could not convert any files. ${failed.length} failed: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? ` +${failed.length - 3} more` : ''}. Some HEIC files from newer iPhones may not be supported.`
        );
      }
    } catch (err) {
      setCompressorError(err instanceof Error ? err.message : 'Compression failed');
    } finally {
      setCompressorProcessing(false);
      setCompressorProgress(null);
    }
  };

  const downloadZip = async () => {
    if (compressedResults.length === 0) return;
    const zip = new JSZip();
    compressedResults.forEach(({ file, blob }) => {
      zip.file(getWebpFilename(file.name), blob);
    });
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compressed-images-${Date.now()}.zip`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalOriginalSize = compressorFiles.reduce((sum, f) => sum + f.size, 0);
  const totalCompressedSize = compressedResults.reduce((sum, r) => sum + r.blob.size, 0);

  if (!user) {
    return (
      <div className="tools-page">
        <h1>Tools</h1>
        <p>Please log in to access these tools.</p>
      </div>
    );
  }

  return (
    <div className="tools-page">
      <AppHeader />
      <div className="tools-page-content">
        <div className="tools-page-header">
          <h1><Wrench size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />Tools</h1>
          <p>Utility tools for your workflow</p>
        </div>

        {isAdmin && (
          <section className="tools-section">
            <h2><Target size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Personal Finance</h2>
            <div className="tools-links">
              <Link to="/tools/lifetime-goals" className="tool-link-card">
                <div className="tool-icon"><Target size={20} /></div>
                <div className="tool-info">
                  <div className="tool-name">Lifetime Goals Calculator</div>
                  <div className="tool-desc">
                    Model retirement, college, health, and lifestyle goals — solve for the income you need today.
                  </div>
                </div>
                <span className="tool-arrow">→</span>
              </Link>
            </div>
          </section>
        )}

        {isFullCrmUser && (
          <section className="tools-section">
            <h2><Scissors size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Estimating</h2>
            <div className="tools-links">
              <Link to="/tools/hand-cut-vs-cnc" className="tool-link-card">
                <div className="tool-icon"><Scissors size={20} /></div>
                <div className="tool-info">
                  <div className="tool-name">Hand cut vs CNC</div>
                  <div className="tool-desc">Compare shop labor to a CNC supplier quote on the same board-foot scope.</div>
                </div>
                <span className="tool-arrow">→</span>
              </Link>
              <Link to="/tools/timber-sizing" className="tool-link-card">
                <div className="tool-icon"><Ruler size={20} /></div>
                <div className="tool-info">
                  <div className="tool-name">Timber Sizing</div>
                  <div className="tool-desc">
                    Span → min stock section or section → max clear span for simple-span sawn timber (half-under).
                  </div>
                </div>
                <span className="tool-arrow">→</span>
              </Link>
              {handCutVsCncExternalUrl.trim() ? (
                <a
                  href={handCutVsCncExternalUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tool-link-card"
                >
                  <div className="tool-icon"><ExternalLink size={20} /></div>
                  <div className="tool-info">
                    <div className="tool-name">Hand cut vs CNC — external</div>
                    <div className="tool-desc">Open the linked spreadsheet, SOP, or supplier portal (configured in Settings → Tools).</div>
                  </div>
                  <span className="tool-arrow">↗</span>
                </a>
              ) : null}
            </div>
          </section>
        )}

        <section className="tools-section">
          <h2><Image size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Image Compressor</h2>
          <div className="compressor-section">
            <div className="compressor-intro">
              <p>Upload one or more images to compress and convert to WebP format. WebP typically reduces file size by 25–35% vs JPEG/PNG. Bulk convert downloads all images as a ZIP file.</p>
              <p className="compressor-heic-note">HEIC from some newer iPhones may fail to convert; those files will be skipped and the rest will process.</p>
            </div>

            <div
              className="compressor-dropzone"
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer.files.length > 0) {
                  addFiles(e.dataTransfer.files);
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,image/heif,.heic,.heif"
                multiple
                onChange={(e) => {
                  const files = e.target.files;
                  if (files && files.length > 0) {
                    addFiles(files);
                    e.target.value = '';
                  }
                }}
                className="compressor-file-input"
                style={compressorFiles.length > 0 ? { pointerEvents: 'none' } : undefined}
              />
              {compressorFiles.length === 0 ? (
                <div className="compressor-placeholder">
                  <Upload size={40} />
                  <p>Drop images here or click to browse</p>
                  <span className="compressor-hint">JPEG, PNG, GIF, WebP, BMP, HEIC — single or multiple</span>
                </div>
              ) : (
                <div className="compressor-files-list">
                  <div className="compressor-files-header">
                    <span className="compressor-files-count">{compressorFiles.length} image{compressorFiles.length !== 1 ? 's' : ''} selected</span>
                    <div className="compressor-files-actions">
                      <button type="button" className="compressor-add-more-btn" onClick={() => fileInputRef.current?.click()}>
                        <Upload size={14} /> Add more
                      </button>
                      <button type="button" className="compressor-clear-btn" onClick={clearAll}>
                        <X size={14} /> Clear all
                      </button>
                    </div>
                  </div>
                  <div className="compressor-files-grid">
                    {compressorFiles.map((file, i) => (
                      <div key={`${file.name}-${i}`} className="compressor-file-chip">
                        <FileImage size={16} />
                        <span className="compressor-chip-name" title={file.name}>{file.name}</span>
                        <span className="compressor-chip-size">{formatFileSize(file.size)}</span>
                        <button
                          type="button"
                          className="compressor-chip-remove"
                          onClick={() => removeFile(i)}
                          aria-label="Remove"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {compressorFiles.length > 0 && (
              <>
                <div className="compressor-options">
                  <div className="compressor-option">
                    <label>Quality (0–1)</label>
                    <input
                      type="range"
                      min="0.3"
                      max="1"
                      step="0.01"
                      value={compressorQuality}
                      onChange={(e) => {
                        setCompressorQuality(parseFloat(e.target.value));
                        setCompressedResults([]);
                      }}
                    />
                    <span className="compressor-value">{compressorQuality.toFixed(2)}</span>
                  </div>
                  <div className="compressor-option">
                    <label>Max width (px)</label>
                    <input
                      type="number"
                      min="100"
                      max="4096"
                      placeholder="No limit"
                      value={compressorMaxWidth}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCompressorMaxWidth(v === '' ? '' : parseInt(v, 10) || '');
                        setCompressedResults([]);
                      }}
                    />
                  </div>
                  <div className="compressor-option">
                    <label>Max height (px)</label>
                    <input
                      type="number"
                      min="100"
                      max="4096"
                      placeholder="No limit"
                      value={compressorMaxHeight}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCompressorMaxHeight(v === '' ? '' : parseInt(v, 10) || '');
                        setCompressedResults([]);
                      }}
                    />
                  </div>
                </div>

                <div className="compressor-actions">
                  <button
                    className="compressor-process-btn"
                    onClick={processAll}
                    disabled={compressorProcessing}
                  >
                    {compressorProcessing
                      ? `Processing… ${compressorProgress ? `${compressorProgress.current}/${compressorProgress.total}` : ''}`
                      : `Compress & Convert ${isBulk ? `(${compressorFiles.length} images)` : 'to WebP'}`}
                  </button>
                </div>

                {compressorProcessing && compressorProgress && (
                  <div className="compressor-progress-bar">
                    <div
                      className="compressor-progress-fill"
                      style={{ width: `${(compressorProgress.current / compressorProgress.total) * 100}%` }}
                    />
                  </div>
                )}

                {compressorError && (
                  <div className="compressor-error">
                    <AlertTriangle size={16} /> {compressorError}
                  </div>
                )}

                {compressedResults.length > 0 && (
                  <div className="compressor-result">
                    <div className="compressor-result-card">
                      <div className="compressor-result-row">
                        <span>Original total:</span>
                        <span>{formatFileSize(totalOriginalSize)}</span>
                      </div>
                      <div className="compressor-result-row">
                        <span>Compressed total (WebP):</span>
                        <span>{formatFileSize(totalCompressedSize)}</span>
                      </div>
                      <div className="compressor-result-row highlight">
                        <span>Saved:</span>
                        <span>
                          {((1 - totalCompressedSize / totalOriginalSize) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    {isBulk ? (
                      <button
                        type="button"
                        className="compressor-download-btn"
                        onClick={downloadZip}
                      >
                        <Download size={18} /> Download as ZIP
                      </button>
                    ) : (
                      <a
                        href={URL.createObjectURL(compressedResults[0].blob)}
                        download={getWebpFilename(compressedResults[0].file.name)}
                        className="compressor-download-btn"
                      >
                        <Download size={18} /> Download WebP
                      </a>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ToolsPage;
